import { CallcenterState } from './types';
import { klemmeWizardSchritt } from './wizardSchritte';

/**
 * Wizard-Sitzung: der angefangene Vorgang uebersteht Reload und Tab-Crash.
 *
 * ENTSCHEID Alex 07.08.2026 — der Reset-bei-Reload aus Bug 1 (31.05.2026) wird
 * bewusst durch eine FORTSETZUNG ersetzt.
 *
 * WAS BUG 1 VERHINDERTE, und was diese Datei deshalb nicht wieder aufreissen
 * darf. Der `callcenter`-Store hat kein `persist`; zurueckgesetzt wurde er
 * damals aber auch nirgends ausser vom "Neuer Anruf"-Knopf in Step 7. Bei
 * SPA-Navigation und bei Logout+Login **im selben Tab** blieben
 * `searchPerformed`/`currentStep`/`wizardData` einer frueheren Sitzung stehen,
 * und der Start-Klick fuehrte direkt in den letzten Schritt statt zur
 * Kontaktsuche — der Agent sass mitten im Vorgang eines fremden Kunden, ohne
 * dass irgendetwas es angesagt haette. Der Fix war `handleStart` →
 * `resetWizard()` plus die Logout-Hygiene in `useClearState`. Ausdruecklich
 * NICHT gebaut wurde damals ein Mount-Reset — der haette einen aktiv laufenden
 * Vorgang gekillt.
 *
 * Der Kern von Bug 1 war also nicht "Zustand ueberlebt", sondern **"Zustand
 * ueberlebt STILL"**. Genau daran setzt dieser Entwurf an:
 *
 *  1. Wiederaufnahme ist immer eine FRAGE, nie ein Automatismus. Das Laden
 *     passiert erst auf Klick (`CallcenterWizard`, Fortsetzen-Dialog). Diese
 *     Datei liefert nur den Stand; sie spielt ihn nie selbst ein.
 *  2. Gespeichert wird in `sessionStorage`, nicht `localStorage`: der Stand
 *     stirbt mit dem Tab und wandert nie in ein anderes Fenster.
 *  3. Der Stand ist an die **User-ID** gebunden. Meldet sich jemand anderes im
 *     selben Tab an, wird er verworfen statt angeboten — das ist der Fall
 *     "Logout+Login (selber Tab)" aus Bug 1, und er traegt hier zusaetzlich
 *     PII. Verlassen kann man sich dabei NICHT auf den Logout-Pfad: der
 *     401-Interceptor (`services/network`) raeumt bei abgelaufenem Token nur
 *     `localStorage` und leitet um — `clearAllStates()` laeuft dort gar nicht.
 *  4. Der Stand verfaellt nach {@link MAX_ALTER_MS}. Ein Vorgang von heute
 *     Morgen ist am Nachmittag kein "angefangener Vorgang" mehr, sondern ein
 *     Fund.
 */

/** Sitzungs-Schluessel. Wird auch vom 401-Zweig in `services/network` geraeumt. */
export const SITZUNG_SCHLUESSEL = 'bw.wizard.sitzung';

/**
 * Formatversion. Hochzaehlen, sobald sich der Zuschnitt von
 * {@link GESPEICHERTE_FELDER} aendert — ein Stand aus einem alten Build wird
 * dann verworfen statt halb eingespielt.
 */
export const SITZUNG_VERSION = 2;

/** Verfallszeit: 60 Minuten (Vorgabe Alex 07.08.2026). */
export const MAX_ALTER_MS = 60 * 60 * 1000;

/**
 * Was mitgespeichert wird — bewusst als Liste und nicht als "alles ausser".
 *
 * Drin ist, was den Vorgang ausmacht: Eingaben, Schritt, Buchungsmodus und die
 * Ergebnisse der Kontaktsuche, aus denen die Pfade C (Recycling) und E
 * (offener Case) leben. Ohne die letzten beiden landete eine fortgesetzte
 * Buchung im Pool statt beim gepinnten Berater — der Stand saehe richtig aus
 * und buchte falsch.
 *
 * DRAUSSEN bleibt alles Fluechtige und alles Abgeleitete: `routedPool` (wird
 * beim Step-7-Mount neu ermittelt), Slots, `bookingResult`, `loading`/`error`,
 * `intakeConflict`, `healthInsurances` (Server-Liste), `modusWahlOffen`
 * (Anzeigezustand) und `practiceModeAvailable` (Server-Flag, kein Wizard-Stand).
 */
const GESPEICHERTE_FELDER = [
  'currentStep',
  'wizardData',
  'foerderanspruch',
  'bookingType',
  'searchPerformed',
  'searchPath',
  'searchBlock',
  'searchOpenCase',
  'searchCandidates',
  'searchReactivation',
  'matchReasons',
  'existingCustomerId',
  'existingCustomerName',
  'existingCustomerNextMeeting',
  'recyclingCustomerId',
  'activeRecycling',
  'activeOpenCase',
  'testMode',
] as const;

export type GespeicherterStand = Pick<CallcenterState, (typeof GESPEICHERTE_FELDER)[number]>;

export interface WizardSitzung {
  version: number;
  gespeichertAm: number;
  userId: number | null;
  stand: GespeicherterStand;
}

/**
 * Ist der Stand es wert, gespeichert zu werden?
 *
 * Zwei Ausloeser, und der zweite ist der leisere: entweder ist der Agent past
 * der Kontaktsuche (`searchPerformed`) — dann liegen Eingaben vor —, ODER er
 * hat den Buchungsmodus gewaehlt. Die Modus-Wahl faellt am Wizard-START, also
 * VOR jeder Sucherei; ohne diesen zweiten Ausloeser waere sie das einzige, was
 * ein Reload wieder verschluckt.
 *
 * Nach einer abgeschlossenen Buchung gibt es nichts fortzusetzen.
 */
export function istSpeicherwuerdig(state: Pick<CallcenterState, 'searchPerformed' | 'bookingType' | 'bookingResult'>): boolean {
  if (state.bookingResult !== null) return false;
  return state.searchPerformed || state.bookingType !== null;
}

/**
 * Traegt der Stand einen angefangenen VORGANG (und nicht nur eine Modus-Wahl)?
 *
 * Nur dann wird gefragt. Eine blosse Modus-Wahl ist keine "angefangene
 * Buchung" — fuer sie einen Dialog aufzumachen waere Laerm, und sichtbar ist
 * sie ohnehin ueber den Badge.
 */
export function istFortsetzbar(sitzung: WizardSitzung | null): boolean {
  return sitzung?.stand.searchPerformed === true;
}

/** Anzeigename fuer den Dialog. Faellt auf einen neutralen Text zurueck. */
export function kundenname(sitzung: WizardSitzung): string {
  const { vorname, nachname } = sitzung.stand.wizardData ?? { vorname: '', nachname: '' };
  const ausWizard = `${vorname ?? ''} ${nachname ?? ''}`.trim();
  return ausWizard || sitzung.stand.existingCustomerName || 'diesen Kontakt';
}

export function speichereWizardSitzung(state: CallcenterState, userId: number | null): void {
  try {
    const stand = GESPEICHERTE_FELDER.reduce((acc, feld) => {
      (acc as Record<string, unknown>)[feld] = state[feld];
      return acc;
    }, {} as GespeicherterStand);

    const sitzung: WizardSitzung = {
      version: SITZUNG_VERSION,
      gespeichertAm: Date.now(),
      userId,
      stand,
    };
    window.sessionStorage.setItem(SITZUNG_SCHLUESSEL, JSON.stringify(sitzung));
  } catch {
    // sessionStorage kann werfen (Safari Private Mode, Storage-Blocker, Quota).
    // Das kostet die Fortsetzbarkeit und sonst nichts — der Wizard laeuft
    // unveraendert weiter. Fail-safe in die sichere Richtung.
  }
}

/**
 * Stand lesen — mit allen drei Riegeln. Liefert `null`, sobald einer greift,
 * und raeumt den Schluessel dann auch weg: ein Stand, den wir nicht anbieten
 * duerfen, hat auch nichts mehr im Speicher zu suchen (er traegt PII).
 *
 * `jetzt` ist injizierbar, damit der Verfall testbar ist, ohne die Uhr zu
 * stellen.
 */
export function ladeWizardSitzung(userId: number | null, jetzt: number = Date.now()): WizardSitzung | null {
  try {
    const roh = window.sessionStorage.getItem(SITZUNG_SCHLUESSEL);
    if (!roh) return null;

    const sitzung = JSON.parse(roh) as WizardSitzung;

    const verworfen =
      sitzung?.version !== SITZUNG_VERSION
      || typeof sitzung.gespeichertAm !== 'number'
      || jetzt - sitzung.gespeichertAm > MAX_ALTER_MS
      || sitzung.userId !== userId
      || typeof sitzung.stand !== 'object'
      || sitzung.stand === null;

    if (verworfen) {
      vergissWizardSitzung();
      return null;
    }

    // Schritt-Index auf die aktuelle Schrittfolge klemmen (N1, 01.10.2026):
    // ein Stand von vor dem Wegfall des Förderrechners traegt 5 oder 6 und
    // bleibt trotzdem fortsetzbar — er landet auf der Zusammenfassung.
    return { ...sitzung, stand: { ...sitzung.stand, currentStep: klemmeWizardSchritt(sitzung.stand.currentStep) } };
  } catch {
    vergissWizardSitzung();
    return null;
  }
}

export function vergissWizardSitzung(): void {
  try {
    window.sessionStorage.removeItem(SITZUNG_SCHLUESSEL);
  } catch {
    /* siehe speichereWizardSitzung */
  }
}
